import { Flag, Trash2, TriangleAlert } from "lucide-react";

const FIELD =
  "w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100";

const SEVERITY = {
  alta: "text-red-700 dark:text-red-400",
  media: "text-amber-700 dark:text-amber-400",
  baixa: "text-gray-600 dark:text-gray-400",
};

/** Configuração do nó selecionado. Fica ao lado do canvas, não dentro da caixa:
 *  a caixa precisa ficar pequena para o fluxo continuar legível. */
const NodeConfigPanel = ({
  node,
  spec,
  operators,
  isStart,
  tags,
  users,
  stages,
  warnings = [],
  onChange,
  onRemove,
  onSetStart,
}) => {
  if (!node) {
    return (
      <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
        Selecione uma caixa para configurar, ou arraste de uma saída até outra
        caixa para ligar as duas.
      </div>
    );
  }

  const renderField = (config) => {
    const value = node[config.key] ?? "";

    if (config.type === "textarea") {
      return (
        <textarea
          className={FIELD}
          rows={4}
          placeholder={config.placeholder}
          value={value}
          onChange={(event) => onChange({ [config.key]: event.target.value })}
        />
      );
    }

    if (config.type === "boolean") {
      return (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="h-4 w-4 accent-green-500"
            checked={Boolean(node[config.key])}
            onChange={(event) => onChange({ [config.key]: event.target.checked })}
          />
          {config.label}
        </label>
      );
    }

    if (config.type === "select") {
      return (
        <select
          className={FIELD}
          value={value}
          onChange={(event) => onChange({ [config.key]: event.target.value })}
        >
          <option value="">Selecione...</option>
          {config.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }

    const lists = { tag: tags, user: users };
    if (lists[config.type]) {
      return (
        <select
          className={FIELD}
          value={value}
          onChange={(event) => onChange({ [config.key]: event.target.value })}
        >
          <option value="">Selecione...</option>
          {lists[config.type].map((item) => (
            <option key={item.id} value={item.id}>{item.name}</option>
          ))}
        </select>
      );
    }

    if (config.type === "stage") {
      return (
        <select
          className={FIELD}
          value={value}
          onChange={(event) => onChange({ [config.key]: event.target.value })}
        >
          <option value="">Selecione...</option>
          {stages.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
        </select>
      );
    }

    return (
      <input
        type={config.type === "number" ? "number" : "text"}
        className={FIELD}
        placeholder={config.placeholder}
        value={value}
        onChange={(event) =>
          onChange({
            [config.key]:
              config.type === "number" ? Number(event.target.value) : event.target.value,
          })
        }
      />
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 p-3 dark:border-gray-700">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">
            {spec?.label}
          </p>
          <input
            className="w-full border-0 bg-transparent p-0 text-sm font-semibold text-gray-900 outline-none dark:text-gray-100"
            value={node.label || ""}
            placeholder={spec?.label}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {!isStart && (
            <button
              onClick={onSetStart}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Começar o fluxo por esta caixa"
            >
              <Flag className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onRemove}
            className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Excluir caixa"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {spec?.fields.map((config) => (
          <div key={config.key}>
            {config.type !== "boolean" && (
              <span className="mb-1 block text-xs text-gray-600 dark:text-gray-400">
                {config.label}
              </span>
            )}
            {renderField(config)}
          </div>
        ))}

        {warnings.map((warning) => (
          <p
            key={warning.id}
            className={`flex items-start gap-1 text-xs ${SEVERITY[warning.severity]}`}
          >
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{warning.label}</strong> (“{warning.match}”) — {warning.explanation}
            </span>
          </p>
        ))}

        {spec?.branching && (
          <div className="rounded-lg bg-gray-50 p-2 dark:bg-gray-800">
            <p className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
              Caminhos
            </p>

            {(node.branches || []).map((branch, index) => (
              <div key={index} className="mb-2 space-y-1 rounded border border-gray-200 p-2 dark:border-gray-700">
                <div className="flex items-center gap-1">
                  <select
                    className={FIELD}
                    value={branch.when || "contains"}
                    onChange={(event) => {
                      const branches = [...(node.branches || [])];
                      branches[index] = { ...branch, when: event.target.value };
                      onChange({ branches });
                    }}
                  >
                    {operators.map((operator) => (
                      <option key={operator.value} value={operator.value}>
                        {operator.label}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() =>
                      onChange({
                        branches: (node.branches || []).filter(
                          (_, position) => position !== index,
                        ),
                      })
                    }
                    className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <input
                  className={FIELD}
                  placeholder="valor comparado"
                  value={branch.value || ""}
                  onChange={(event) => {
                    const branches = [...(node.branches || [])];
                    branches[index] = { ...branch, value: event.target.value };
                    onChange({ branches });
                  }}
                />

                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {branch.next
                    ? "Ligado — arraste a saída da direita para trocar o destino."
                    : "Sem destino: arraste a saída da direita até outra caixa."}
                </p>
              </div>
            ))}

            <button
              onClick={() =>
                onChange({ branches: [...(node.branches || []), { when: "contains" }] })
              }
              className="text-xs text-blue-600 hover:underline dark:text-blue-400"
            >
              + adicionar caminho
            </button>
          </div>
        )}

        {!spec?.terminal && (
          <p className="rounded-lg bg-gray-50 p-2 text-[11px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            {node.next
              ? "Saída inferior ligada. Para trocar, arraste-a até outra caixa."
              : "Saída inferior sem ligação: o roteiro termina nesta caixa."}
          </p>
        )}
      </div>
    </div>
  );
};

export default NodeConfigPanel;
